import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { MediaProject } from "../../../types/MediaProject";
import {
  listenToMediaProjects,
  deleteMediaProject,
} from "../../../database/community/mediaProjects";
import {
  TikTokSendButton,
  LinkedInPostButton,
  MetaPublishButtons,
  YouTubePostButton,
} from "./publish/PublishButtons";
import SchedulePostDialog from "./publish/SchedulePostDialog";
import { useSocialConnections } from "./hooks/useSocialConnections";

type MediaProjectsViewProps = {
  communityId?: string;
};

const formatDuration = (sec?: number): string => {
  if (!sec || sec <= 0) return "";
  const total = Math.round(sec);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
};

const STATUS_VARIANT: Record<MediaProject["status"], string> = {
  draft: "var(--color-text-tertiary)",
  ready: "var(--color-success)",
  scheduled: "var(--color-info)",
  posted: "var(--color-accent)",
};

// ─── Styles ─────────────────────────────────────────────────────────────────

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: var(--space-3);
`;

const CardRoot = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: var(--radius-lg);
  border: 1px solid var(--color-border-subtle);
  background: var(--color-surface);
  overflow: hidden;
`;

const Preview = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  background: var(--color-surface-subtle);
  display: flex;
  overflow: hidden;

  img,
  video {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const SlideStrip = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  gap: 1px;

  img {
    flex: 1 1 0;
    min-width: 0;
    object-fit: cover;
  }
`;

const MoreBadge = styled.span`
  position: absolute;
  right: 6px;
  bottom: 6px;
  padding: 1px 6px;
  border-radius: var(--radius-pill);
  background: rgba(0, 0, 0, 0.62);
  color: #fff;
  font-family: var(--font-sans);
  font-size: 10px;
  font-weight: var(--font-weight-semibold);
`;

const KindBadge = styled.span`
  position: absolute;
  left: 6px;
  top: 6px;
  padding: 1px 6px;
  border-radius: var(--radius-pill);
  background: rgba(0, 0, 0, 0.62);
  color: #fff;
  font-family: var(--font-sans);
  font-size: 10px;
  font-weight: var(--font-weight-semibold);
  letter-spacing: 0.02em;
`;

const DeleteButton = styled.button`
  position: absolute;
  top: 6px;
  right: 6px;
  appearance: none;
  border: none;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  font-size: 15px;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: var(--color-danger);
  }
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: var(--space-2) var(--space-3) var(--space-3);
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-2);
  justify-content: space-between;
`;

const TitleText = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StatusDot = styled.span<{ $color: string }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  flex-shrink: 0;
  font-family: var(--font-sans);
  font-size: 10px;
  color: var(--color-text-secondary);

  &::before {
    content: "";
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: ${(p) => p.$color};
  }
`;

const Caption = styled.div`
  font-family: var(--font-sans);
  font-size: 11px;
  line-height: 1.35;
  color: var(--color-text-secondary);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: var(--space-8) var(--space-4);
  text-align: center;
  color: var(--color-text-secondary);
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
`;

// ─── Card ───────────────────────────────────────────────────────────────────

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 2px;
`;

const ProjectCard: React.FC<{
  project: MediaProject;
  onDelete: (p: MediaProject) => void;
  communityId?: string;
  tiktokConnected: boolean;
  linkedinConnected: boolean;
  igConnected: boolean;
  fbPageConnected: boolean;
  youtubeConnected: boolean;
  youtubeChannelLabel?: string;
}> = ({
  project,
  onDelete,
  communityId,
  tiktokConnected,
  linkedinConnected,
  igConnected,
  fbPageConnected,
  youtubeConnected,
  youtubeChannelLabel,
}) => {
  const slides = project.slides || [];
  const preview =
    project.kind === "carousel" ? (
      slides.length ? (
        <SlideStrip>
          {slides.slice(0, 3).map((s, i) => (
            <img key={`${s.assetId}-${i}`} src={s.mediaUrl} alt="" loading="lazy" />
          ))}
        </SlideStrip>
      ) : null
    ) : project.outputUrl ? (
      <video src={project.outputUrl} preload="metadata" muted playsInline />
    ) : project.thumbnailUrl ? (
      <img src={project.thumbnailUrl} alt="" loading="lazy" />
    ) : null;

  return (
    <CardRoot>
      <Preview>
        {preview}
        <KindBadge>{project.kind === "carousel" ? "CAROUSEL" : "REEL"}</KindBadge>
        {project.kind === "carousel" && slides.length > 3 && (
          <MoreBadge>+{slides.length - 3}</MoreBadge>
        )}
        {project.kind === "reel" && project.durationSec ? (
          <MoreBadge>{formatDuration(project.durationSec)}</MoreBadge>
        ) : null}
        <DeleteButton
          type="button"
          aria-label="Delete project"
          onClick={() => onDelete(project)}
        >
          ×
        </DeleteButton>
      </Preview>
      <Body>
        <TitleRow>
          <TitleText>
            {project.title ||
              (project.kind === "carousel"
                ? `Carousel · ${slides.length} slide${slides.length === 1 ? "" : "s"}`
                : "Reel")}
          </TitleText>
          <StatusDot $color={STATUS_VARIANT[project.status]}>
            {project.status}
          </StatusDot>
        </TitleRow>
        {project.caption && <Caption>{project.caption}</Caption>}
        {project.kind === "carousel" ? (
          <Actions>
            <TikTokSendButton
              communityId={communityId}
              project={project}
              connected={tiktokConnected}
            />
          </Actions>
        ) : null}
        <Actions>
          <LinkedInPostButton
            communityId={communityId}
            project={project}
            connected={linkedinConnected}
          />
        </Actions>
        <Actions>
          <MetaPublishButtons
            communityId={communityId}
            project={project}
            igConnected={igConnected}
            pageConnected={fbPageConnected}
          />
        </Actions>
        <Actions>
          <YouTubePostButton
            communityId={communityId}
            project={project}
            connected={youtubeConnected}
            channelLabel={youtubeChannelLabel}
          />
        </Actions>
        <Actions>
          <SchedulePostDialog
            communityId={communityId}
            project={project}
            firstParty={{
              instagram: igConnected,
              facebook: fbPageConnected,
              tiktok: tiktokConnected,
              linkedin: linkedinConnected,
              youtube: youtubeConnected,
            }}
            youtubeChannelLabel={youtubeChannelLabel}
          />
        </Actions>
        <Actions>
          <SchedulePostDialog
            communityId={communityId}
            project={project}
            firstParty={{
              instagram: igConnected,
              facebook: fbPageConnected,
              tiktok: tiktokConnected,
              linkedin: linkedinConnected,
              youtube: youtubeConnected,
            }}
            youtubeChannelLabel={youtubeChannelLabel}
            asDraft
            triggerLabel="Queue as draft"
          />
        </Actions>
      </Body>
    </CardRoot>
  );
};

// ─── View ─────────────────────────────────────────────────────────────────

export const MediaProjectsView: React.FC<MediaProjectsViewProps> = ({
  communityId,
}) => {
  const [projects, setProjects] = useState<MediaProject[]>([]);
  const [loading, setLoading] = useState(true);
  // Connection state drives the per-project publish buttons; the hook refreshes
  // on window focus so a connection made on the Connections tab shows up here.
  const {
    youtube,
    flags: {
      tiktok: tiktokConnected,
      linkedin: linkedinConnected,
      instagram: igConnected,
      facebook: fbPageConnected,
      youtube: youtubeConnected,
    },
  } = useSocialConnections(communityId);

  useEffect(() => {
    if (!communityId) {
      setProjects([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const unsubscribe = listenToMediaProjects(
      communityId,
      (next) => {
        setProjects(next);
        setLoading(false);
      },
      () => setLoading(false)
    );
    return () => unsubscribe();
  }, [communityId]);

  const handleDelete = async (project: MediaProject) => {
    if (!communityId) return;
    if (
      !window.confirm(
        `Delete this ${project.kind}? The source library assets are not affected.`
      )
    )
      return;
    setProjects((prev) => prev.filter((p) => p.id !== project.id));
    try {
      await deleteMediaProject(communityId, project.id);
    } catch (e) {
      console.error("Failed to delete project:", e);
    }
  };

  return (
    <>
      {loading ? (
        <EmptyState>Loading projects…</EmptyState>
      ) : projects.length === 0 ? (
        <EmptyState>
          No composed projects yet. Ask Claude to build a carousel or reel from
          your library — it'll show up here.
        </EmptyState>
      ) : (
        <Grid>
          {projects.map((project) => (
            <ProjectCard
              key={project.id}
              project={project}
              onDelete={handleDelete}
              communityId={communityId}
              tiktokConnected={tiktokConnected}
              linkedinConnected={linkedinConnected}
              igConnected={igConnected}
              fbPageConnected={fbPageConnected}
              youtubeConnected={youtubeConnected}
              youtubeChannelLabel={
                youtube?.channelTitle || youtube?.channelHandle || undefined
              }
            />
          ))}
        </Grid>
      )}
    </>
  );
};

export default MediaProjectsView;
